import { loginRedirect } from "../lib/loginRedirect";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { useLocale } from "../contexts/LocaleContext";
import { config } from "../config";
import "./phone-login.css";

/** Login and linking share a code form; the server binds purpose and identity. */
export function PhoneLogin({ link = false, onLinked }: { link?: boolean; onLinked?: () => void }) {
  const { t, language } = useLocale();
  const { refreshIdentity } = useAuth();
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const el = dialog.current;
    if (open && el && !el.open) {
      el.showModal();
      el.querySelector<HTMLInputElement>("input:not([disabled])")?.focus();
    }
    return () => { el?.close(); };
  }, [open]);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(true);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(0);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  useEffect(() => {
    let alive = true;
    apiFetch("/auth/phone/config").then(async r => {
      if (r.ok) { const data = await r.json(); if (alive) setEnabled(data.enabled === true); }
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    if (!wait) return;
    const timer = window.setTimeout(() => setWait(n => Math.max(0, n - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [wait]);
  async function request(action: "start" | "verify") {
    if (busy || config.phoneLoginPending) return;
    setBusy(true); setError("");
    try {
      const response = await apiFetch(`/auth/phone/${link ? "link-" : ""}${action}`, {
        method: "POST", body: JSON.stringify(action === "start" ? { phone, remember, language } : { code }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        const known = ["invalid_phone", "country_unavailable", "too_many_attempts", "invalid_code", "expired_code", "phone_conflict", "sms_failed", "forbidden"];
        if (response.status === 429) setWait(60);
        throw new Error(known.includes(result.code) ? result.code : "unavailable");
      }
      if (action === "start") { setSent(true); setCode(""); setWait(60); }
      else if (link) { setDone(true); onLinked?.(); }
      else {
        const identity = await refreshIdentity();
        const target = result.tos === null ? "/register" : loginRedirect(sessionStorage.getItem("postLoginRedirect"), "/start", identity?.roles);
        if (result.tos !== null) sessionStorage.removeItem("postLoginRedirect");
        navigate(target.startsWith("/") ? target : `/${target}`, { replace: true });
      }
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }
  function submit(e: FormEvent) { e.preventDefault(); void request(sent ? "verify" : "start"); }
  if (!enabled) return null;
  const content = <div className="phone-login">
    <h2 id={link ? "phone-link-title" : "phone-login-title"}>{t(link ? "phone.linkTitle" : "phone.title")}</h2>
    {config.phoneLoginPending ? <div role="status">
      <p>{t("phone.pendingNotice")}</p>
      <p>{t("phone.pendingAlternatives")}</p>
      {!link && <button type="button" onClick={() => setOpen(false)}>{t("phone.understood")}</button>}
    </div> : done ? <p role="status">{t("phone.linked")}</p> : <form onSubmit={submit}>
      <p>{t(link ? "phone.linkHint" : "phone.hint")}</p>
      {!link && <p className="phone-existing">{t("phone.existingHint")}</p>}
      <label htmlFor={link ? "link-phone" : "login-phone"}>{t("phone.number")}</label>
      <input id={link ? "link-phone" : "login-phone"} type="tel" autoComplete="tel" placeholder="+43 664 1234567"
        value={phone} onChange={e => setPhone(e.target.value)} required disabled={sent || busy} maxLength={40} />
      {!sent && <p className="phone-note">{t("phone.countries")}</p>}
      {!link && <label className="phone-check"><input type="checkbox" checked={remember} disabled={sent || busy}
        onChange={e => setRemember(e.target.checked)} />{t("phone.remember")}</label>}
      {sent && <>
        <p role="status">{t("phone.sent")}</p>
        <label htmlFor={link ? "link-code" : "login-code"}>{t("phone.code")}</label>
        <input id={link ? "link-code" : "login-code"} type="text" inputMode="numeric" autoComplete="one-time-code"
          pattern="[0-9]{6}" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ""))}
          required disabled={busy} autoFocus />
      </>}
      {error && <p role="alert">{t(`phone.errors.${error}`) === `phone.errors.${error}` ? t("phone.errors.unavailable") : t(`phone.errors.${error}`)}</p>}
      <button type="submit" disabled={busy}>{t(busy ? "phone.busy" : sent ? "phone.confirm" : "phone.send")}</button>
      {sent && <div className="phone-actions">
        <button type="button" disabled={busy || wait > 0} onClick={() => void request("start")}>{wait ? t("phone.wait", { seconds: wait }) : t("phone.resend")}</button>
        <button type="button" disabled={busy} onClick={() => { setSent(false); setCode(""); setError(""); }}>{t("phone.change")}</button>
      </div>}
    </form>}
  </div>;
  if (link) return content;
  return <>
    <button type="button" className="social-login-btn phone-login-btn" aria-haspopup="dialog" onClick={() => setOpen(true)}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <rect x="6" y="2" width="12" height="20" rx="2" /><path d="M10 18h4" />
      </svg>
      {t("phone.title")}
    </button>
    <dialog ref={dialog} className="phone-login-dialog" aria-labelledby="phone-login-title" onCancel={e => {
      if (busy) e.preventDefault(); else setOpen(false);
    }}>
      {open && <>
        <button type="button" className="phone-login-close" aria-label={t("phone.close")} disabled={busy} onClick={() => setOpen(false)}>×</button>
        {content}
      </>}
    </dialog>
  </>;
}
